import {
  customerEngineEditionV1Schema,
  type CustomerEngineEditionV1,
} from "@lifescapsule/contracts";
import { ApiError, apiRequest } from "../../../shared/api";

/** `/engine-editions/compare`: what changed between two retained proofs (no restore offered). */
export type EngineEditionCompare = {
  bookId: string;
  leftProofId: string;
  rightProofId: string;
  sourceChanged: boolean;
  planChanged: boolean;
  mediaChanged: boolean;
  coverChanged: boolean;
  outputsChanged: boolean;
};

export function parseEngineEditionCompare(value: unknown): EngineEditionCompare {
  const body = value as Record<string, unknown> | null;
  const text = (key: string) => typeof body?.[key] === "string" && String(body[key]).length > 0;
  const flag = (key: string) => typeof body?.[key] === "boolean";
  if (
    !body ||
    !["bookId", "leftProofId", "rightProofId"].every(text) ||
    !["sourceChanged", "planChanged", "mediaChanged", "coverChanged", "outputsChanged"].every(
      flag,
    ) ||
    body.restore !== "NO_UNIVERSAL_RESTORE_USE_FAMILY_REVISION_COMMAND"
  )
    throw new Error("The edition comparison could not be read.");
  return {
    bookId: String(body.bookId),
    leftProofId: String(body.leftProofId),
    rightProofId: String(body.rightProofId),
    sourceChanged: body.sourceChanged === true,
    planChanged: body.planChanged === true,
    mediaChanged: body.mediaChanged === true,
    coverChanged: body.coverChanged === true,
    outputsChanged: body.outputsChanged === true,
  };
}

export type EditionDimension = keyof CustomerEngineEditionV1["currentness"];

export type ApprovedEditionComparison =
  /** No accepted edition for this book (never approved, or its record wasn't kept). */
  | { state: "NO_APPROVED_EDITION" }
  /** Approved before page-by-page capture existed: no retained proof to compare against. */
  | { state: "NOT_CAPTURED"; editionId: string | null }
  | {
      state: "READY";
      accepted: CustomerEngineEditionV1;
      /** The book's current preview proof, when it has one. */
      workingProofId: string | null;
      /** The current preview couldn't be read, so whether a newer one exists isn't known. */
      workingUnavailable: boolean;
      /** Proof-to-proof changes; null when there is no other preview, or it is the same one. */
      changes: EngineEditionCompare | null;
      /** The proof-to-proof read failed; the per-part currentness still stands. */
      changesUnavailable: boolean;
    };

const book = (bookId: string) => `/books/${encodeURIComponent(bookId)}/engine-editions`;

async function edition(bookId: string, idToken: string, view?: "ACCEPTED") {
  const response = await apiRequest<{ data: unknown }>(
    `${book(bookId)}${view ? `?${new URLSearchParams({ view }).toString()}` : ""}`,
    idToken,
  );
  const parsed = customerEngineEditionV1Schema.parse(response.data);
  if (parsed.bookId !== bookId) throw new Error("This edition belongs to another book.");
  return parsed;
}

function unavailable(error: unknown) {
  return error instanceof ApiError && error.status === 404;
}

/** The accepted edition, read on its own (also the source of page mapping for the reader). */
export async function loadAcceptedEdition(bookId: string, idToken: string) {
  try {
    return await edition(bookId, idToken, "ACCEPTED");
  } catch (error) {
    if (unavailable(error)) return null;
    throw error;
  }
}

/**
 * Compares the accepted edition with the current preview. Each part (words, page plan, pictures,
 * preview, cover) comes from the accepted read's own currentness; the proof comparison adds what
 * differs between the two retained previews when both exist.
 */
export async function loadApprovedEditionComparison(
  bookId: string,
  idToken: string,
): Promise<ApprovedEditionComparison> {
  const accepted = await loadAcceptedEdition(bookId, idToken);
  if (!accepted || (!accepted.editionId && !accepted.proofId))
    return { state: "NO_APPROVED_EDITION" };
  if (accepted.availability === "HISTORICAL_EVIDENCE_UNAVAILABLE")
    return { state: "NOT_CAPTURED", editionId: accepted.editionId };
  if (
    accepted.availability === "LEGACY_READER_ONLY" ||
    accepted.availability === "INCOMPATIBLE" ||
    !accepted.proofId
  )
    return { state: "NOT_CAPTURED", editionId: accepted.editionId };
  let workingProofId: string | null = null;
  let workingUnavailable = false;
  let changes: EngineEditionCompare | null = null;
  let changesUnavailable = false;
  try {
    workingProofId = (await edition(bookId, idToken)).proofId;
  } catch (error) {
    // No current edition at all is an answer (no newer preview); any other failure is not.
    if (!unavailable(error)) workingUnavailable = true;
  }
  try {
    if (workingProofId && workingProofId !== accepted.proofId) {
      const parameters = new URLSearchParams({ left: accepted.proofId, right: workingProofId });
      const response = await apiRequest<{ data: unknown }>(
        `${book(bookId)}/compare?${parameters.toString()}`,
        idToken,
      );
      changes = parseEngineEditionCompare(response.data);
      if (
        changes.bookId !== bookId ||
        changes.leftProofId !== accepted.proofId ||
        changes.rightProofId !== workingProofId
      )
        throw new Error("The comparison belongs to another edition.");
    }
  } catch {
    changes = null;
    changesUnavailable = true;
  }
  return {
    state: "READY",
    accepted,
    workingProofId,
    workingUnavailable,
    changes,
    changesUnavailable,
  };
}
